/**
 * scripts-utilities-dom.js
 * -----------------------------------------------------------------------
 * Small, dependency-free DOM helpers reused across future components.
 * Exposed on `window.AniMarket.dom` so any inline script or component
 * script can use them without a module bundler.
 * ----------------------------------------------------------------------- */
(function () {
  'use strict';

  /** @param {string} selector @param {ParentNode} [scope] */
  function qs(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  /** @param {string} selector @param {ParentNode} [scope] */
  function qsa(selector, scope) {
    return Array.from((scope || document).querySelectorAll(selector));
  }

  /**
   * Debounce a function, delaying invocation until `wait` ms have
   * elapsed since the last call.
   * @param {Function} fn
   * @param {number} wait
   */
  function debounce(fn, wait) {
    var timeoutId;
    return function debounced() {
      var context = this;
      var args = arguments;
      clearTimeout(timeoutId);
      timeoutId = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  /**
   * Throttle a function so it runs at most once every `wait` ms.
   * @param {Function} fn
   * @param {number} wait
   */
  function throttle(fn, wait) {
    var isThrottled = false;
    var lastArgs = null;
    return function throttled() {
      var context = this;
      var args = arguments;
      if (isThrottled) {
        lastArgs = args;
        return;
      }
      fn.apply(context, args);
      isThrottled = true;
      setTimeout(function () {
        isThrottled = false;
        if (lastArgs) {
          fn.apply(context, lastArgs);
          lastArgs = null;
        }
      }, wait);
    };
  }

  /**
   * Trap keyboard focus within a container (for future dialogs/drawers).
   * @param {HTMLElement} container
   * @param {KeyboardEvent} event
   */
  function trapFocus(container, event) {
    if (event.key !== 'Tab') return;
    var focusable = qsa(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      container
    ).filter(function (el) {
      return !el.hasAttribute('disabled') && el.offsetParent !== null;
    });
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /** Generates a short, non-cryptographic unique id for DOM relationships. */
  function generateId(prefix) {
    return (prefix || 'am') + '-' + Math.random().toString(36).slice(2, 9);
  }

  /**
   * Formats a raw cents value using the shop's real money format string
   * (`window.AniMarket.moneyFormat`, set from `shop.money_format` in
   * layout/theme.liquid) — the same format Shopify itself uses, so a
   * client-side price built from a `.js` product/variant payload matches
   * every server-rendered price on the page.
   * @param {number} cents
   */
  function formatMoney(cents) {
    var format = (window.AniMarket && window.AniMarket.moneyFormat) || '${{amount}}';
    var value = (cents / 100).toFixed(2);
    var placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);

    if (placeholder && placeholder[1] === 'amount_no_decimals') {
      value = String(Math.round(cents / 100));
    } else if (placeholder && placeholder[1] === 'amount_with_comma_separator') {
      value = (cents / 100).toFixed(2).replace('.', ',');
    }

    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  window.AniMarket = window.AniMarket || {};
  window.AniMarket.dom = {
    qs: qs,
    qsa: qsa,
    debounce: debounce,
    throttle: throttle,
    trapFocus: trapFocus,
    generateId: generateId,
  };
  window.AniMarket.formatMoney = formatMoney;
})();
